import { useState, useEffect, useRef, useCallback } from "react";
import { message } from "antd";
import { useSelector } from "react-redux";
import { store } from "../redux/configStore";
import { LoadDataCart } from "../redux/Login/Login";
import { fetchOrderCount, fetchLowStockCount } from "../api/orderPing";
import {
  normalizeEvent,
  pushNotification,
  groupByDate,
  toastText,
  toastMethod,
  persistItems,
  loadItems,
  clearItems,
} from "../utils/notificationMapper";

/* ─── Config ─── */
const POLL_INTERVAL = 25_000; // 25s between polls
const TOAST_COOLDOWN = 15_000; // min 15s between toasts

/**
 * Data-source mode.
 * "polling" — current implementation, uses orderPing.js
 * "ws"      — future WebSocket implementation
 *
 * To switch: change this constant + implement wsDataSource below.
 */
const DATA_SOURCE_MODE = "polling"; // future: "ws"

/* ═══════════════════════════════════════════════════════════
 * DATA-SOURCE LAYER
 *
 * Each data source calls `onEvents(rawEvents[])` when new
 * events are detected. The hook's core logic is transport-agnostic.
 *
 * Raw event shape (from any source):
 *   { type: "NEW_ORDER", count: 3 }
 *   { type: "ORDER_CANCELLED", order_id: "123", channel: "shopee" }
 *
 * ═══════════════════════════════════════════════════════════ */

/**
 * POLLING data source.
 *
 * Compares order count deltas and emits NEW_ORDER events.
 * Now receives idw from Redux (via getIdw callback) instead of cookies.
 */
const LOW_STOCK_CHECK_INTERVAL = 5; // check low stock every 5th poll (~2 min)

function createPollingSource({ onEvents, baselineRef, getIdw }) {
  let intervalId = null;
  let lastIdw = null;
  let pollCount = 0;
  let lowStockBaseline = null;

  const poll = async () => {
    const idw = getIdw();
    if (!idw) return;

    // Detect idw change → reset baseline
    if (lastIdw && lastIdw !== idw) {
      baselineRef.current = null;
      lowStockBaseline = null;
    }
    lastIdw = idw;
    pollCount++;

    try {
      const events = [];

      // Order count check (every poll)
      const { count } = await fetchOrderCount(idw);

      // First poll: set baseline, no events
      if (baselineRef.current === null) {
        baselineRef.current = count;
      } else {
        const diff = count - baselineRef.current;
        if (diff > 0) {
          baselineRef.current = count;
          events.push({ type: "NEW_ORDER", count: diff });
        }
      }

      // Low stock check (every Nth poll to reduce API calls)
      if (pollCount % LOW_STOCK_CHECK_INTERVAL === 0) {
        try {
          const { count: lowCount } = await fetchLowStockCount(idw);
          if (lowStockBaseline === null) {
            lowStockBaseline = lowCount;
          } else if (lowCount > lowStockBaseline) {
            const diff = lowCount - lowStockBaseline;
            lowStockBaseline = lowCount;
            events.push({ type: "LOW_STOCK", count: diff });
          } else {
            lowStockBaseline = lowCount;
          }
        } catch {
          // Silent — low stock check is non-critical
        }
      }

      if (events.length > 0) onEvents(events);
    } catch {
      // Network error — silently skip this poll cycle
    }
  };

  return {
    start() {
      if (intervalId) return;
      poll();
      intervalId = setInterval(poll, POLL_INTERVAL);
    },
    stop() {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    },
    /** Manual re-poll for force-refresh. Returns updated count. */
    async rePoll() {
      const idw = getIdw();
      if (!idw) return;
      const { count } = await fetchOrderCount(idw);
      baselineRef.current = count;
    },
  };
}

/**
 * WEBSOCKET data source — STUB.
 *
 * When ready to implement:
 *   1. Set DATA_SOURCE_MODE = "ws"
 *   2. Fill in the connect/disconnect logic below
 *   3. Call onEvents() with the typed events from the WS payload
 *   4. The normalize layer + UI pick it up automatically
 */
// function createWsDataSource({ onEvents, getIdw }) {
//   let ws = null;
//   return {
//     start() {
//       const idw = getIdw();
//       ws = new WebSocket(`wss://your-domain/ws/notifications?idw=${idw}`);
//       ws.onmessage = (e) => {
//         const events = JSON.parse(e.data);
//         onEvents(events);
//       };
//     },
//     stop() {
//       ws?.close();
//       ws = null;
//     },
//     async rePoll() { /* no-op for WS, or call REST fallback */ },
//   };
// }

/* ═══════════════════════════════════════════════════════════
 * HOOK CORE — transport-agnostic
 * ═══════════════════════════════════════════════════════════ */

/**
 * useOrderNotifier — manages notification state from any data source.
 *
 * Returns:
 *   badge        — number of unread events
 *   items        — NotificationItem[] (newest first)
 *   groups       — grouped by date for timeline UI
 *   loading      — true while a manual refresh is in progress
 *   markRead     — () => void — clears badge
 *   forceRefresh — () => Promise<void> — manual refresh button
 */
export default function useOrderNotifier() {
  const { userData } = useSelector((s) => s.persistedReducer.Login);
  const idw = userData?.idw;

  const [badge, setBadge] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  // Refs to survive re-renders
  const baselineRef = useRef(null);
  const lastToastRef = useRef(0);
  const idwRef = useRef(null);
  const sourceRef = useRef(null);

  /** Stable getter so polling source always reads latest idw */
  const idwLatestRef = useRef(idw);
  useEffect(() => { idwLatestRef.current = idw; }, [idw]);
  const getIdw = useCallback(() => idwLatestRef.current, []);

  /* ─── Side effect: dispatch + toast ─── */
  const dispatchEffect = useCallback((rawEvent) => {
    const currentIdw = idwLatestRef.current;

    // Auto-refresh cart data so the order list updates
    if (currentIdw) {
      store.dispatch(LoadDataCart({ idw: currentIdw }));
    }

    // Toast with cooldown
    if (Date.now() - lastToastRef.current > TOAST_COOLDOWN) {
      lastToastRef.current = Date.now();
      const method = toastMethod(rawEvent);
      message[method]({
        content: toastText(rawEvent),
        duration: 3,
      });
    }
  }, []);

  /* ─── Event handler: called by any data source ─── */
  const handleEvents = useCallback(
    (rawEvents) => {
      for (const raw of rawEvents) {
        // 1. Normalize into NotificationItem
        const item = normalizeEvent(raw);

        // 2. Push into list
        setItems((prev) => {
          const next = pushNotification(prev, item);
          // Persist to sessionStorage
          const currentIdw = idwLatestRef.current;
          if (currentIdw) persistItems(currentIdw, next);
          return next;
        });

        // 3. Bump badge
        setBadge((prev) => prev + (raw.count ?? 1));

        // 4. Side effects (toast, dispatch)
        dispatchEffect(raw);
      }
    },
    [dispatchEffect]
  );

  /* ─── IDW change detection ─── */
  const resetForIdw = useCallback((newIdw) => {
    baselineRef.current = null;
    setBadge(0);

    // Load persisted items for new idw
    const cached = loadItems(newIdw);
    setItems(cached);
  }, []);

  /* ─── Polling lifecycle with visibility API ─── */
  useEffect(() => {
    if (!idw) return;

    // Restore persisted items on mount
    const cached = loadItems(idw);
    if (cached.length > 0) setItems(cached);
    idwRef.current = idw;

    // Create data source based on mode
    const source = createPollingSource({
      onEvents: (events) => {
        // Check idw change before processing
        const currentIdw = idwLatestRef.current;
        if (idwRef.current && idwRef.current !== currentIdw) {
          resetForIdw(currentIdw);
          idwRef.current = currentIdw;
          return; // skip events from old idw
        }
        if (currentIdw) idwRef.current = currentIdw;
        handleEvents(events);
      },
      baselineRef,
      getIdw,
    });
    sourceRef.current = source;

    const startSource = () => source.start();
    const stopSource = () => source.stop();

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        // Check idw change when tab becomes visible
        const currentIdw = idwLatestRef.current;
        if (idwRef.current && idwRef.current !== currentIdw) {
          resetForIdw(currentIdw);
          idwRef.current = currentIdw;
        }
        startSource();
      } else {
        stopSource();
      }
    };

    if (document.visibilityState === "visible") {
      startSource();
    }

    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopSource();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [idw, handleEvents, resetForIdw, getIdw]);

  /* ─── Public: mark all as read ─── */
  const markRead = useCallback(() => {
    setBadge(0);
  }, []);

  /* ─── Public: force refresh ─── */
  const forceRefresh = useCallback(async () => {
    const currentIdw = idwLatestRef.current;
    if (!currentIdw) return;

    setLoading(true);
    try {
      // Refresh cart data
      await store.dispatch(LoadDataCart({ idw: currentIdw }));

      // Re-poll to update baseline
      if (sourceRef.current) {
        await sourceRef.current.rePoll();
      }

      message.success("Đã cập nhật đơn hàng");
    } catch {
      message.error("Không thể cập nhật");
    } finally {
      setLoading(false);
    }
  }, []);

  /* ─── Public: clear all (for logout) ─── */
  const clearAll = useCallback(() => {
    const currentIdw = idwLatestRef.current;
    setBadge(0);
    setItems([]);
    if (currentIdw) clearItems(currentIdw);
  }, []);

  /* ─── Derived: grouped items for timeline UI ─── */
  const groups = groupByDate(items);

  return {
    badge,
    items,
    groups,
    loading,
    markRead,
    forceRefresh,
    clearAll,
    /** @deprecated Use `groups` for timeline UI. Kept for backward compat. */
    _dataSourceMode: DATA_SOURCE_MODE,
  };
}
